/**
 * WorkspaceApiKeysTab Component
 *
 * Allows workspace admins to manage API Keys for external integrations.
 * Shows a table of existing keys with status, last-used date, and actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import { Workspace } from '../../../types/workspace';
import { ApiKey } from '../../../types/apiKey';
import { listApiKeys, updateApiKey } from '../../../services/apiKeyService';
import { CreateApiKeyModal } from '../modals/CreateApiKeyModal';
import { RevokeApiKeyModal } from '../modals/RevokeApiKeyModal';

interface WorkspaceApiKeysTabProps {
  workspace: Workspace;
}

export const WorkspaceApiKeysTab: React.FC<WorkspaceApiKeysTabProps> = ({ workspace }) => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listApiKeys(workspace.id);
      setKeys(data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar chaves de API');
    } finally {
      setIsLoading(false);
    }
  }, [workspace.id]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleToggleActive = async (key: ApiKey) => {
    setTogglingId(key.id);
    try {
      const updated = await updateApiKey(workspace.id, key.id, { is_active: !key.is_active });
      setKeys((prev) => prev.map((k) => (k.id === key.id ? updated : k)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao alterar status da chave');
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreateSuccess = (newKey: ApiKey) => {
    setKeys((prev) => [newKey, ...prev]);
  };

  const handleRevokeSuccess = (revokedId: number) => {
    setKeys((prev) => prev.filter((k) => k.id !== revokedId));
    setKeyToRevoke(null);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (key: ApiKey) => {
    if (!key.expires_at) return false;
    return new Date(key.expires_at) < new Date();
  };

  const formatNumber = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}K`
      : String(n);

  const isTokensExhausted = (key: ApiKey) =>
    key.max_tokens !== null && key.tokens_used >= key.max_tokens;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F4CC04]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-[#F4CC04]/20 rounded-lg">
            <Key className="w-5 h-5 text-[#F4CC04]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Chaves de API</h3>
            <p className="text-sm text-[#888888]">
              Autentique integrações externas com este workspace ({keys.length}{' '}
              {keys.length === 1 ? 'chave' : 'chaves'})
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadKeys}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
            title="Recarregar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#F4CC04] text-black rounded-lg text-sm font-medium hover:bg-[#e6be04] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Gerar nova chave
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-300">
          As chaves de API concedem acesso ao workspace em nome do usuário que as criou. Revogue
          imediatamente qualquer chave comprometida.
        </p>
      </div>

      {/* Table */}
      {keys.length === 0 ? (
        <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-12 text-center">
          <Key className="w-12 h-12 text-[#888888] mx-auto mb-3" />
          <p className="text-[#888888] font-medium">Nenhuma chave de API criada</p>
          <p className="text-xs text-[#666666] mt-1">
            Gere uma chave para autenticar sistemas externos neste workspace
          </p>
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#333333]">
                <th className="text-left px-4 py-3 text-[#888888] font-medium">Nome</th>
                <th className="text-left px-4 py-3 text-[#888888] font-medium">Identificador</th>
                <th className="text-left px-4 py-3 text-[#888888] font-medium">Status</th>
                <th className="text-left px-4 py-3 text-[#888888] font-medium">Tokens usados</th>
                <th className="text-left px-4 py-3 text-[#888888] font-medium">Último uso</th>
                <th className="text-left px-4 py-3 text-[#888888] font-medium">Expira em</th>
                <th className="text-left px-4 py-3 text-[#888888] font-medium">Criada em</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#333333]">
              {keys.map((key) => (
                <tr key={key.id} className="hover:bg-[#222222] transition-colors">
                  {/* Name + notes */}
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{key.name}</div>
                    {key.notes && (
                      <div className="text-xs text-[#888888] mt-0.5 truncate max-w-[180px]">
                        {key.notes}
                      </div>
                    )}
                  </td>

                  {/* Public identifier */}
                  <td className="px-4 py-3">
                    <code className="text-xs text-[#F4CC04] bg-[#F4CC04]/10 px-2 py-1 rounded font-mono">
                      {key.api_key}
                    </code>
                  </td>

                  {/* Status badge */}
                  <td className="px-4 py-3">
                    {isExpired(key) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full font-medium">
                        <Clock className="w-3 h-3" />
                        Expirada
                      </span>
                    ) : key.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                        Ativa
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#444444] text-[#888888] rounded-full font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#888888] inline-block" />
                        Inativa
                      </span>
                    )}
                  </td>

                  {/* Token usage */}
                  <td className="px-4 py-3">
                    {key.max_tokens === null ? (
                      <span className="text-[#888888] text-xs">{formatNumber(key.tokens_used)} / ∞</span>
                    ) : isTokensExhausted(key) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full font-medium">
                        Esgotado
                      </span>
                    ) : (
                      <div>
                        <span className="text-xs text-[#cccccc]">
                          {formatNumber(key.tokens_used)}{' '}
                          <span className="text-[#888888]">/ {formatNumber(key.max_tokens)}</span>
                        </span>
                        <div className="mt-1 h-1 w-24 bg-[#333333] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#F4CC04] rounded-full"
                            style={{ width: `${Math.min((key.tokens_used / key.max_tokens) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Last used */}
                  <td className="px-4 py-3 text-[#888888]">{formatDate(key.last_used_at)}</td>

                  {/* Expires at */}
                  <td className="px-4 py-3">
                    {key.expires_at ? (
                      <span className={isExpired(key) ? 'text-orange-400' : 'text-[#888888]'}>
                        {formatDate(key.expires_at)}
                      </span>
                    ) : (
                      <span className="text-[#888888]">Nunca</span>
                    )}
                  </td>

                  {/* Created at */}
                  <td className="px-4 py-3 text-[#888888]">{formatDate(key.created_at)}</td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {/* Toggle active */}
                      <button
                        onClick={() => handleToggleActive(key)}
                        disabled={togglingId === key.id || isExpired(key)}
                        className="p-1.5 rounded-lg hover:bg-[#333333] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={key.is_active ? 'Desativar chave' : 'Ativar chave'}
                      >
                        {key.is_active ? (
                          <ToggleRight className="w-5 h-5 text-green-400" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-[#888888]" />
                        )}
                      </button>

                      {/* Revoke */}
                      <button
                        onClick={() => setKeyToRevoke(key)}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#888888] hover:text-red-400 transition-colors"
                        title="Revogar chave"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateApiKeyModal
          workspaceId={workspace.id}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {keyToRevoke && (
        <RevokeApiKeyModal
          apiKey={keyToRevoke}
          onClose={() => setKeyToRevoke(null)}
          onSuccess={handleRevokeSuccess}
        />
      )}
    </div>
  );
};

export default WorkspaceApiKeysTab;
